import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { formatWageSummary } from "../utils/wage-fields";

type ProviderOrganizationWageSetsSectionProps = {
  organizationBusinessUnitId: string;
  providerUserId: string;
};

/** The wage sets covering a hired provider today, linking to the wage sets page. */
export function ProviderOrganizationWageSetsSection({
  organizationBusinessUnitId,
  providerUserId,
}: ProviderOrganizationWageSetsSectionProps) {
  const trpc = useTRPC();
  const organizationWageSetsQuery = useQuery(
    trpc.organizationWageSet.listForProvider.queryOptions({
      organizationBusinessUnitId,
      providerUserId,
    }),
  );

  return (
    <section
      aria-labelledby="provider-organization-wage-sets-heading"
      className="mx-4 my-4 rounded-2xl border border-slate-200/80 bg-white p-4 md:mx-8"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2
          id="provider-organization-wage-sets-heading"
          className="text-base font-semibold text-slate-900"
        >
          Wage sets
        </h2>
        <Link
          to="/org/bu/$organizationBusinessUnitId/jobs/wage-sets"
          params={{ organizationBusinessUnitId }}
          className="text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          Manage wage sets
        </Link>
      </div>
      {organizationWageSetsQuery.isLoading && (
        <p className="mt-2 text-sm text-slate-500">Loading wage sets...</p>
      )}
      {organizationWageSetsQuery.error && (
        <p className="mt-2 text-sm text-red-600">Couldn't load wage sets.</p>
      )}
      {organizationWageSetsQuery.data &&
        organizationWageSetsQuery.data.length === 0 && (
          <p className="mt-2 text-sm text-slate-600">
            No wage sets apply to this provider today.
          </p>
        )}
      {organizationWageSetsQuery.data &&
        organizationWageSetsQuery.data.length > 0 && (
          <ul className="mt-2 space-y-2">
            {organizationWageSetsQuery.data.map((orgWageSet) => (
              <li
                key={`${orgWageSet.organizationWageSetId}-${orgWageSet.versionId}`}
                className="text-sm"
              >
                <Link
                  to="/org/bu/$organizationBusinessUnitId/jobs/wage-sets/$organizationWageSetId"
                  params={{
                    organizationBusinessUnitId,
                    organizationWageSetId: orgWageSet.organizationWageSetId,
                  }}
                  className="font-medium text-slate-900 hover:underline"
                >
                  {orgWageSet.name}
                </Link>
                <span className="text-slate-600">
                  {" "}
                  · {formatWageSummary(orgWageSet.wages)}
                </span>
              </li>
            ))}
          </ul>
        )}
    </section>
  );
}
